import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, CircularProgress } from '@mui/material';

/**
 * DrawerActions — botões padronizados de "Cancelar" + "Confirmar" para
 * os drawers de ação da plataforma (Receber, Cancelar conta, Pix/Boleto,
 * Excluir, etc).
 *
 * Visual coerente com o FilterDrawer / FilterForm:
 *   - Cancelar: text ghost cinza
 *   - Confirmar: contained azul primary com shadow
 *
 * Loading com bloqueio:
 *   - Quando `loading=true`, ambos os botões ficam disabled
 *   - O botão "Confirmar" mostra CircularProgress no startIcon
 *   - Previne duplo-clique e ação enquanto comunica com backend
 *
 * Uso típico:
 *   <DrawerActions
 *     onCancel={handleClose}
 *     onConfirm={handleSubmit}
 *     loading={communicating}
 *     confirmLabel="Receber"
 *     confirmIcon={<CheckIcon />}
 *   />
 */
function DrawerActions(props) {
  const {
    cancelLabel,
    confirmLabel,
    onCancel,
    onConfirm,
    loading,
    confirmDisabled,
    confirmIcon,
    confirmColor,
    sx,
  } = props;
  const isLoading = Boolean(loading);
  const isDisabled = isLoading || Boolean(confirmDisabled);

  // Paleta do botão de confirmar — default azul primary. `danger` deixa
  // vermelho (pra confirmação de Cancelar/Excluir conta).
  const palette =
    confirmColor === 'danger'
      ? {
          bg: '#D7372A',
          bgHover: '#a82b20',
          shadow: '0 6px 18px rgba(215, 55, 42, 0.25)',
          shadowHover: '0 8px 22px rgba(215, 55, 42, 0.32)',
          disabledBg: 'rgba(215, 55, 42, 0.4)',
        }
      : {
          bg: '#0080ad',
          bgHover: '#197997',
          shadow: '0 6px 18px rgba(0, 128, 173, 0.25)',
          shadowHover: '0 8px 22px rgba(0, 128, 173, 0.32)',
          disabledBg: 'rgba(0, 128, 173, 0.4)',
        };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        ...sx,
      }}
    >
      <Button
        onClick={onCancel}
        disabled={isLoading}
        variant="text"
        sx={{
          fontWeight: 600,
          textTransform: 'none',
          color: 'rgba(96, 125, 139, 0.95)',
          px: 2,
          borderRadius: '10px',
          '&:hover': { bgcolor: 'rgba(0, 128, 173, 0.06)' },
          '&.Mui-disabled': {
            color: 'rgba(96, 125, 139, 0.45)',
          },
        }}
      >
        {cancelLabel}
      </Button>
      <Button
        onClick={onConfirm}
        disabled={isDisabled}
        variant="contained"
        startIcon={
          isLoading ? (
            <CircularProgress size={16} sx={{ color: '#fff' }} />
          ) : (
            confirmIcon
          )
        }
        sx={{
          bgcolor: palette.bg,
          color: '#fff',
          fontWeight: 600,
          textTransform: 'none',
          fontSize: '0.95rem',
          px: 2.5,
          py: 1,
          borderRadius: '10px',
          boxShadow: palette.shadow,
          '&:hover': {
            bgcolor: palette.bgHover,
            boxShadow: palette.shadowHover,
          },
          '&.Mui-disabled': {
            bgcolor: palette.disabledBg,
            color: '#fff',
          },
        }}
      >
        {confirmLabel}
      </Button>
    </Box>
  );
}

DrawerActions.defaultProps = {
  cancelLabel: 'Cancelar',
  confirmLabel: 'Confirmar',
  onCancel: () => null,
  onConfirm: () => null,
  loading: false,
  confirmDisabled: false,
  confirmIcon: null,
  confirmColor: 'primary',
  sx: null,
};

DrawerActions.propTypes = {
  cancelLabel: PropTypes.string,
  confirmLabel: PropTypes.string,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
  loading: PropTypes.bool,
  confirmDisabled: PropTypes.bool,
  confirmIcon: PropTypes.node,
  confirmColor: PropTypes.oneOf(['primary', 'danger']),
  // eslint-disable-next-line react/forbid-prop-types
  sx: PropTypes.object,
};

export default DrawerActions;
